import { ChevronDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TRAVEL_CATEGORIES, useTravelCategories } from "./constants";
import { useTranslation } from "react-i18next";

const CategorySelect = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const categories = useTravelCategories();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCategory = categories.find((cat) => cat.value === value) || {
    icon: "🌎",
    label: t("ideas.form.category_label"),
    value: "",
  };

  const handleCategoryClick = (categoryValue) => {
    // If clicking the already selected category, unselect it
    if (value === categoryValue) {
      onChange("");
    } else {
      onChange(categoryValue);
    }
    setIsOpen(false);
  };

  return (
    <div className="tw-relative" ref={dropdownRef}>
      <motion.div
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className="tw-mt-2 tw-w-full tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border-0 tw-bg-gray-50 tw-px-4 tw-py-3.5 tw-text-gray-900 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-gray-200 focus:tw-ring-2 focus:tw-ring-[deepskyblue] tw-transition-all tw-cursor-pointer"
      >
        <div className="tw-flex tw-items-center tw-space-x-2">
          <span className="tw-text-xl">{selectedCategory.icon}</span>
          <span>{selectedCategory.label}</span>
        </div>
        <ChevronDown
          className={`tw-w-5 tw-h-5 tw-text-gray-400 tw-transition-transform ${
            isOpen ? "tw-rotate-180" : ""
          }`}
        />
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="tw-absolute tw-z-10 tw-mt-2 tw-w-full tw-rounded-xl tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 tw-overflow-hidden"
          >
            <div className="tw-max-h-60 tw-overflow-y-auto">
              {categories.map((category) => (
                <motion.div
                  key={category.value}
                  onClick={() => handleCategoryClick(category.value)}
                  className={`tw-w-full tw-flex tw-items-center tw-space-x-2 tw-px-4 tw-py-3 tw-text-left tw-cursor-pointer hover:tw-text-[deepskyblue] hover:tw-bg-sky-50 tw-transition-colors ${
                    value === category.value
                      ? "tw-bg-sky-50 tw-text-[deepskyblue]"
                      : "tw-text-gray-700"
                  }`}
                >
                  <span className="tw-text-xl">{category.icon}</span>
                  <span>{category.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategorySelect;
